import { FC, useCallback, MouseEvent, useState, useMemo } from 'react';

import {
  Button,
  Calendar,
  Chip,
  DateValue,
  Icon,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { formatDate } from 'src/utils/formatDate';
import { getDateTime } from 'src/utils/getDateTime';

import { TooltipWrapper } from '../TooltipWrapper/TooltipWrapper';

import classes from './DatesPicker.module.scss';

interface DateChipProps {
  date: string;
  onRemove: (date: string) => void;
  isRemovable: boolean;
  error?: string;
}

const DateChip: FC<DateChipProps> = ({
  date,
  onRemove,
  isRemovable,
  error,
}) => {
  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onRemove(date);
    },
    [date, onRemove],
  );

  const formatted = useMemo(() => formatDate(new Date(date)), [date]);

  return formatted ? (
    <TooltipWrapper disableHover={!error} title={error}>
      <Chip
        label={formatted}
        key={date}
        endAdornment={
          isRemovable ? (
            <Icon onClick={handleRemove} iconName={Icons.Cancel} />
          ) : undefined
        }
        className={clsx(!!error && classes.errorChip)}
      />
    </TooltipWrapper>
  ) : null;
};

export interface DatesPickerProps {
  dates: Set<string>;
  excludedDates?: Set<string>;
  onAddDate: (value: string) => void;
  onRemoveDate: (value: string) => void;
  minDate?: Date;
  maxDate?: Date;
  errors: Map<string, string>;
  minCount?: number;
}

export const DatesPicker: FC<DatesPickerProps> = ({
  dates,
  excludedDates,
  onAddDate,
  onRemoveDate,
  minDate,
  maxDate,
  errors,
  minCount,
}) => {
  const [calendarIsOpen, setCalendarIsOpen] = useState(false);

  const openCalendar = useCallback(() => {
    setCalendarIsOpen(true);
  }, []);

  const closeCalendar = useCallback(() => {
    setCalendarIsOpen(false);
  }, []);

  const addDate = useCallback(
    (date: DateValue) => {
      if (typeof date !== 'string') return;

      onAddDate(date);
      closeCalendar();
    },
    [closeCalendar, onAddDate],
  );

  const excludeDate = useCallback(
    (value: Date) => {
      const dateToExclude = getDateTime({
        date: new Date(value),
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      }).toISOString();
      return !!dateToExclude && !!excludedDates?.has(dateToExclude);
    },
    [excludedDates],
  );

  return (
    <div className={classes.container}>
      <div className={classes.datesContainer}>
        {[...dates].map(date => (
          <DateChip
            key={date}
            date={date}
            onRemove={onRemoveDate}
            isRemovable={minCount ? dates.size > minCount : true}
            error={errors.get(date)}
          />
        ))}
      </div>
      <div className={classes.selectDatesContainer}>
        {calendarIsOpen ? (
          <div className={classes.calendarContainer}>
            <Calendar
              value=''
              onSelectDate={addDate}
              minValue={minDate?.toISOString()}
              maxValue={maxDate?.toISOString()}
              excludeDate={excludeDate}
              onOutsideClick={closeCalendar}
            />
          </div>
        ) : (
          <div className={classes.openCalendarButtonContainer}>
            <Button
              size='medium'
              variant='plain'
              endIcon={<Icon size='large' iconName={Icons.Add} />}
              onClick={openCalendar}
            >
              Добавить дату
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
